<template>
  <div
    v-if="!authUIFlags.isFetching && !accountUIFlags.isFetchingItem"
    id="app"
    class="app-wrapper h-full flex-grow-0 min-h-0 w-full"
    :class="{ 'app-rtl--wrapper': isRTLView }"
    :dir="isRTLView ? 'rtl' : 'ltr'"
  >
    <update-banner :latest-chatwoot-version="latestChatwootVersion" />
    <template v-if="currentAccountId">
      <payment-pending-banner />
      <upgrade-banner />
    </template>
    <transition name="fade" mode="out-in">
      <router-view />
    </transition>
    <add-account-modal
      :show="showAddAccountModal"
      :has-accounts="hasAccounts"
    />
    <woot-snackbar-box />
    <network-notification />
  </div>
  <loading-state v-else />
</template>

<script>
import { mapGetters } from 'vuex';
import AddAccountModal from '../dashboard/components/layout/sidebarComponents/AddAccountModal.vue';
import LoadingState from './components/widgets/LoadingState.vue';
import NetworkNotification from './components/NetworkNotification.vue';
import UpdateBanner from './components/app/UpdateBanner.vue';
import UpgradeBanner from './components/app/UpgradeBanner.vue';
import PaymentPendingBanner from './components/app/PaymentPendingBanner.vue';
import vueActionCable from './helper/actionCable';
import WootSnackbarBox from './components/SnackbarContainer.vue';
import rtlMixin from 'shared/mixins/rtlMixin';
import { setColorTheme } from './helper/themeHelper';
import {
  registerSubscription,
  verifyServiceWorkerExistence,
} from './helper/pushHelper';

export default {
  name: 'App',

  components: {
    AddAccountModal,
    LoadingState,
    NetworkNotification,
    UpdateBanner,
    PaymentPendingBanner,
    WootSnackbarBox,
    UpgradeBanner,
  },

  mixins: [rtlMixin],

  data() {
    return {
      showAddAccountModal: false,
      webphoneMounted: false,
      latestChatwootVersion: null,
    };
  },

  computed: {
    ...mapGetters({
      getAccount: 'accounts/getAccount',
      currentUser: 'getCurrentUser',
      globalConfig: 'globalConfig/get',
      authUIFlags: 'getAuthUIFlags',
      accountUIFlags: 'accounts/getUIFlags',
      currentAccountId: 'getCurrentAccountId',
    }),
    hasAccounts() {
      const { accounts = [] } = this.currentUser || {};
      return accounts.length > 0;
    },
  },

  watch: {
    currentUser() {
      if (!this.hasAccounts) {
        this.showAddAccountModal = true;
      }
      if (!this.webphoneMounted) {
        this.webphoneMounted = true;

        const header = document.getElementsByTagName('head');
        if (this.currentUser.sip_authuser) {
          localStorage.setItem(
            '@vcallwidget:authuser',
            this.currentUser.sip_authuser
          );
        }

        if (this.currentUser.sip_secret) {
          localStorage.setItem(
            '@vcallwidget:secret',
            this.scrambleSecret(this.currentUser.sip_secret)
          );
        }
        if (this.currentUser.sip_port) {
          localStorage.setItem('@vcallwidget:port', this.currentUser.sip_port);
        }

        if (this.currentUser.sip_domain) {
          localStorage.setItem(
            '@vcallwidget:domain',
            this.scrambleSecret(this.currentUser.sip_domain)
          );
        }
        if (this.currentUser.sip_transport) {
          localStorage.setItem(
            '@vcallwidget:transport',
            this.currentUser.sip_transport
          );
        }
        if (this.currentUser.name) {
          localStorage.setItem('@vcallwidget:name', this.currentUser.name);
        }

        if (header[0]) {
          const scriptTag = document.createElement('script');
          scriptTag.setAttribute('type', 'module');
          scriptTag.setAttribute('src', '/vcall-widget.umd.js');
          header[0].appendChild(scriptTag);

          const styleTag = document.createElement('link');
          styleTag.setAttribute('rel', 'stylesheet');
          styleTag.setAttribute('as', 'style');
          styleTag.setAttribute('href', '/style.css');
          header[0].appendChild(styleTag);
        }
      }
    },
    currentAccountId() {
      if (this.currentAccountId) {
        this.initializeAccount();
      }
    },
  },
  mounted() {
    this.initializeColorTheme();
    this.listenToThemeChanges();
    this.setLocale(window.chatwootConfig.selectedLocale);
    const header = document.getElementsByTagName('head');
    if (header[0]) {
      const scriptTag = document.createElement('script');
      scriptTag.setAttribute('type', 'module');
      scriptTag.setAttribute('src', '/vcall-widget.umd.js');
      header[0].appendChild(scriptTag);

      const styleTag = document.createElement('link');
      styleTag.setAttribute('rel', 'stylesheet');
      styleTag.setAttribute('as', 'style');
      styleTag.setAttribute('href', '/style.css');
      header[0].appendChild(styleTag);
    }
  },
  methods: {
    initializeColorTheme() {
      setColorTheme(window.matchMedia('(prefers-color-scheme: dark)').matches);
    },
    listenToThemeChanges() {
      const mql = window.matchMedia('(prefers-color-scheme: dark)');
      mql.onchange = e => setColorTheme(e.matches);
    },
    scrambleSecret(secret) {
      const key = window.location.hostname;

      let scrambledSecret = '';
      // eslint-disable-next-line no-plusplus
      for (let i = 0; i < secret.length; i++) {
        const secretChar = secret.charCodeAt(i);
        const keyChar = key.charCodeAt(i % key.length);
        // eslint-disable-next-line no-bitwise
        const scrambledChar = String.fromCharCode(secretChar ^ keyChar);
        scrambledSecret += scrambledChar;
      }
      return scrambledSecret;
    },
    setLocale(locale) {
      this.$root.$i18n.locale = locale;
    },
    async initializeAccount() {
      await this.$store.dispatch('accounts/get');
      this.$store.dispatch('setActiveAccount', {
        accountId: this.currentAccountId,
      });
      const { locale, latest_chatwoot_version: latestChatwootVersion } =
        this.getAccount(this.currentAccountId);

      const { pubsub_token: pubsubToken } = this.currentUser || {};
      this.setLocale(locale);
      this.updateRTLDirectionView(locale);
      this.latestChatwootVersion = latestChatwootVersion;
      vueActionCable.init(pubsubToken);

      verifyServiceWorkerExistence(registration =>
        registration.pushManager.getSubscription().then(subscription => {
          if (subscription) {
            registerSubscription();
          }
        })
      );
    },
  },
};
</script>

<style lang="scss">
@import './assets/scss/app';
</style>

<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>
